import { useEffect, useState, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users as UsersIcon } from "@phosphor-icons/react";

function initials(name) {
  const src = (name || "?").trim();
  return src
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

/**
 * OnlineChip — shows a live "who's online" pill.
 *
 * Fetches /api/presence once on mount, then subscribes to SSE `presence`
 * events to stay in sync. Keeps its own EventSource (lightweight — server
 * already tracks subscribers, multi-connection de-dupe is not a concern).
 *
 * Renders as a compact pill in the header with:
 *   • pulsing green dot
 *   • overlapping avatar stack (up to 3) + "+N" overflow
 *   • popover showing the full list
 */
export default function OnlineChip({ currentUserId }) {
  const [onlineIds, setOnlineIds] = useState([]);
  const esRef = useRef(null);
  const allUsersRef = useRef({});

  const rebuildUsersFromIds = (ids) => ids.map((id) => allUsersRef.current[id]).filter(Boolean);

  useEffect(() => {
    let cancelled = false;

    const fetchInitial = async () => {
      try {
        const { data } = await api.get("/presence");
        if (cancelled) return;
        (data.users || []).forEach((u) => {
          allUsersRef.current[u.id] = u;
        });
        setOnlineIds(data.online_users || []);
      } catch (_) {}
    };

    const openStream = () => {
      const token = localStorage.getItem("wh_token");
      if (!token) return;
      try { esRef.current?.close(); } catch (_) {}
      const es = new EventSource(`${API_BASE}/stream?token=${encodeURIComponent(token)}`);
      esRef.current = es;
      es.onmessage = (evt) => {
        try {
          const { type, data } = JSON.parse(evt.data);
          if (type === "presence" && Array.isArray(data?.online_users)) {
            setOnlineIds(data.online_users);
            // If a newly-online user isn't in our directory, re-fetch /presence
            // (which includes their metadata). Guarded to avoid loops.
            const missing = data.online_users.some((id) => !allUsersRef.current[id]);
            if (missing) fetchInitial();
          }
        } catch (_) {}
      };
      es.onerror = () => {
        try { es.close(); } catch (_) {}
        setTimeout(() => openStream(), 3000);
      };
    };

    fetchInitial();
    openStream();

    return () => {
      cancelled = true;
      try { esRef.current?.close(); } catch (_) {}
    };
  }, []);

  const users = rebuildUsersFromIds(onlineIds);
  // Put current user first (if they're in the list), then others.
  const sortedUsers = [...users].sort((a, b) => {
    if (a.id === currentUserId) return -1;
    if (b.id === currentUserId) return 1;
    return (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "");
  });

  const count = sortedUsers.length;
  const preview = sortedUsers.slice(0, 3);
  const remaining = Math.max(0, count - preview.length);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="online-chip"
          className="hidden md:flex items-center gap-1.5 h-8 px-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 transition-colors text-white/85"
          title={`${count} online`}
        >
          <span className="relative h-2 w-2 shrink-0">
            <span
              className={`absolute inset-0 rounded-full ${count > 0 ? "bg-[#10B981] live-dot" : "bg-white/30"}`}
            />
          </span>
          <UsersIcon size={13} weight="bold" className="opacity-70" />
          {preview.length > 0 ? (
            <div className="flex -space-x-1.5 ml-0.5">
              {preview.map((u) => (
                <Avatar
                  key={u.id}
                  className="h-5 w-5 ring-1 ring-[#0B0F14]"
                  title={u.full_name || u.email}
                >
                  <AvatarFallback className="bg-[#25D366] text-[9px] text-white">
                    {initials(u.full_name || u.email)}
                  </AvatarFallback>
                </Avatar>
              ))}
            </div>
          ) : null}
          <span className="text-[11px] font-medium">
            {count === 0 ? "0 online" : remaining > 0 ? `+${remaining}` : count}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0" data-testid="online-chip-popover">
        <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between">
          <div className="text-xs font-semibold">Online now</div>
          <div className="text-[10px] text-[var(--muted)]">{count} member{count === 1 ? "" : "s"}</div>
        </div>
        {count === 0 ? (
          <div className="px-3 py-5 text-xs text-[var(--muted)] text-center">
            No one else is online right now.
          </div>
        ) : (
          <ul className="max-h-72 overflow-auto py-1">
            {sortedUsers.map((u) => (
              <li
                key={u.id}
                className="px-3 py-2 flex items-center gap-2.5"
                data-testid={`online-user-${u.id}`}
              >
                <div className="relative">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-[10px]">
                      {initials(u.full_name || u.email)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[#10B981] ring-2 ring-[var(--surface)] live-dot" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">
                    {u.full_name || u.email}
                    {u.id === currentUserId && (
                      <span className="ml-1 text-[9px] text-[var(--muted)]">(you)</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[var(--muted)] truncate">
                    {u.role || "agent"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
